import moment from "moment";
import {Client, Notice, NoticeExecution, NoticeType, Step, Taxpayer, User} from "../../../swaggerServices";
import {asMap} from "../../shared/util/asMap";
import {INotice} from "./types";
import {makeName} from "../../shared/util/makeName";

export const parseMetadataValue = (m, spec, docshareBaseUrl) => {
  const v = m.value != null ? JSON.parse(m.value) : null;
  // TODO(mike): Handle dates better? Need the metadata inputSpecs to do that
  const isArray = v instanceof Array;
  const isObject = !isArray && v != null && typeof v === "object";
  const value = isObject ? JSON.stringify(v, null, 2) : v; // show JSON as a pretty object string for now
  return {
    noticeMetadata: m,
    isArray,
    isObject,
    value,
    link: spec && spec.dataType === "docshareId" && value ? `${docshareBaseUrl}/imanage/dialogs/link/d/${value}` : null,
  };
};

export const formatDocshareLink = (docshareBaseUrl, jobId) => {
  return `${docshareBaseUrl}/imanage/dialogs/link/d/${jobId}`;
};

export const asNumber = x => {
  if (x == null) {
    return null;
  }
  const n = Number(x);
  return Number.isFinite(n) ? n : null;
};

interface NoticeDataPieces {
  notices?: Notice[];
  noticeExecutions?: NoticeExecution[];
  clients?: Client[];
  users?: User[];
  steps?: Step[];
  noticeTypes?: NoticeType[];
  taxpayers?: Taxpayer[];
}

const formatDate = dateStr => {
  const m = moment(dateStr);
  return m.isValid() ? m.format("DD MMM YY") : null;
};

const makeIssueStr = (n: Notice, latestExecution?: NoticeExecution) => {
  if (n.stepId < 0) {
    return "";
  } else if (n.hasException) {
    return latestExecution ? latestExecution.exceptionMessage : "";
  } else if (n.status) {
    return "Unknown server error";
  } else if (latestExecution && !latestExecution.finishedAt) {
    return "Processing";
  } else {
    return "Queued";
  }
};

export const constructNotices = ({ notices, noticeExecutions, clients, users, steps, noticeTypes, taxpayers }: NoticeDataPieces): INotice[] => {
  const executionsById = asMap(noticeExecutions, ne => ne.id);
  const clientsById = asMap(clients, c => c.id);
  const usersById = asMap(users, u => u.id);
  const stepsById = asMap(steps, s => s.id);
  const noticeTypesById = asMap(noticeTypes, nt => nt.id);
  const taxpayersByAtlasId = asMap(taxpayers, tp => tp.atlasId);

  const fullNotices = notices.map(n => {
    const step = stepsById.get(n.stepId);
    const latestExecution = executionsById.get(n.latestExecutionId);
    const taxpayer = taxpayersByAtlasId.get(n.atlasId);
    return {
      ...n,
      latestExecution,
      assignedUser: usersById.get(n.assignedUserId),
      ownerUser: usersById.get(n.ownerUserId),
      client: clientsById.get(n.clientId),
      dateStr: formatDate(n.date),
      dueDateStr: formatDate(n.dueDate),
      step: {
        ...step,
        nextStep: step.nextStepId ? stepsById.get(step.nextStepId) : step,
      },
      noticeType: noticeTypesById.get(n.noticeTypeId),
      taxpayer,
      periodStr: `${n.year || ""} ${n.period && n.period.indexOf("Q") >= 0 ? n.period : ""}`,
      checkboxSelected: false,
      issueStr: makeIssueStr(n, latestExecution),
      taxpayerName: taxpayer ? makeName(taxpayer) : (n.partyEntityName || ""),
      ownerHovered: false,
      assignedToHovered: false,
    };
  });
  return fullNotices;
};

const currencyFormatter = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD", // this will just put a $ sign in front, if you use AUD it'll be like A$123.00
  useGrouping: true, // comma every 3 digits
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

export const formatCurrency = x => {
  if (x == null || x === "") {
    return null;
  }
  const n = Number(x);
  if (Number.isFinite(n)) {
    if (n === 0) {
      return "nil";
    }
    // format as currency, negative numbers are represented with parenthenses (accounting syntax)
    const out = currencyFormatter.format(x);
    return out.charAt(0) === "-" ? `(${out.slice(1)})` : out;
  }
  return null;
};
